import { h, Fragment } from "preact";
import {
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from "preact/hooks";
import { omit, throttle } from "lodash";

import {
  AccessibilityMenu,
  LanguageOption,
} from "./_implementation/AccessibilityMenu";
import { CaptionFetcher } from "./_implementation/CaptionFetcher";
import {
  accessibilityOptionsReducer,
  AccessibilityOptionsState,
  defaultAccessibilityOptionsState,
  TranscriptType,
} from "./_implementation/accessibilityOptionsReducer";
import { ClientAttributes } from "./_implementation/getClientAttributes";
import { getTargetHeight } from "./_implementation/getTargetHeight";
import { captionDirectionFor } from "./_implementation/languageDirection";
import { AccessibilityMenuUrlParser } from "./_implementation/AccessibilityMenuUrlParser";
import {
  fetchManifest,
  Manifest,
  resolveCaptionStream,
} from "./_implementation/Manifest";
import { urlParamsEnabled } from "./_implementation/urlParams";

/**
 * The message shown while a translated stream has not yet produced captions.
 * Distinct from the source "not started" copy so a viewer understands the event
 * is running but this language is not available yet.
 */
const TRANSLATION_NOT_AVAILABLE_MESSAGE =
  "Translation is not yet available for this language";

/**
 * The root component
 */
export function App({
  lineCount,
  liveAsrUniqueKey,
  summarizationKey,
  showAccessibilityMenu,
  showCompleteTranscript,
  pollAfterEvent,
  transcriptContainerHeight,
  useUppercase,
  delaySeconds,
  defaultLanguage,
}: ClientAttributes) {
  const [showScrollBottomButton, setShowScrollBottomButton] = useState(false);
  const [
    scrollToBottomOnTranscriptChange,
    setScrollToBottomOnTranscriptChange,
  ] = useState(true);
  const [message, setMessage] = useState("");
  const [transcript, setTranscript] = useState("");
  const [manifest, setManifest] = useState<Manifest | null>(null);

  // These options need to match exactly the values in the OPTIONS
  // constants in AccessibilityMenu.tsx, or their selections will appear empty
  // on page load.
  // TODO: Enforce the behavior described above.
  const initialAccessibilityOptionsState: AccessibilityOptionsState =
    defaultAccessibilityOptionsState;

  const [accessibilityOptionsState, accessibilityOptionsDispatch] = useReducer(
    accessibilityOptionsReducer,
    initialAccessibilityOptionsState
  );

  const targetEl = useRef<HTMLDivElement>(null);

  const CAPTION_KEY_MAP: Record<TranscriptType, string | undefined> = {
    verbatim: liveAsrUniqueKey,
    summarized: summarizationKey,
  };

  const selectedLanguage = accessibilityOptionsState.language;

  // The source-vs-translated stream decision lives in one place. Source (and
  // every no-manifest / unselected state) keeps the existing unsuffixed key, so
  // captioning-only events are unchanged; a translation hops to the per-language
  // key.
  const { key: captionsKey, isTranslation } = resolveCaptionStream(
    manifest,
    CAPTION_KEY_MAP[accessibilityOptionsState.transcriptType],
    selectedLanguage
  );

  // A translated stream that has not produced captions yet reads differently
  // from a source stream that has not started.
  const notStartedMessage = isTranslation
    ? TRANSLATION_NOT_AVAILABLE_MESSAGE
    : undefined;

  // The language for locale-aware casing and the container `lang` attribute.
  // Only set once the manifest resolves and a language is selected.
  const captionLanguage = selectedLanguage;

  // The container `dir`, derived from the caption language so it shares the same
  // gating: absent until a language is selected, and absent for left-to-right
  // languages, so only right-to-left captions change the markup.
  const captionDirection = captionDirectionFor(captionLanguage);

  // Selector options, source first (VE-2). Empty without a manifest, so the
  // selector never renders for a captioning-only event.
  const languageOptions: LanguageOption[] = manifest
    ? [
        {
          code: manifest.machine_translation.source_language,
          isSource: true,
        },
        ...manifest.machine_translation.target_languages.map((code) => ({
          code,
          isSource: false,
        })),
      ]
    : [];
  const hasTargetLanguages = languageOptions.some((opt) => !opt.isSource);

  /**
   * Whether or not the transcript should include character count based paragraphs
   */
  const insertParagraphs = showCompleteTranscript && !lineCount;

  // The component singleton pattern below guarantees an imperative class instance
  // only gets created once
  // https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
  const captionFetcher = useRef<CaptionFetcher | null>(null);
  function getCaptionFetcher() {
    if (captionFetcher.current === null) {
      captionFetcher.current = new CaptionFetcher({
        insertParagraphs,
        captionsKey,
        delaySeconds,
        language: captionLanguage,
        notStartedMessage,
        setMessage,
        setTranscript,
        pollAfterEvent,
        useUppercase,
      });
    }
    return captionFetcher.current;
  }

  function setAccessibilityOptions() {
    accessibilityOptionsDispatch({
      type: "SET_ACCESSIBILITY_OPTIONS",
      newValue: new AccessibilityMenuUrlParser().parse(),
    });
  }

  useEffect(() => {
    setAccessibilityOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start or update the CaptionFetcher. Keyed on captionsKey only: resolving a
  // manifest whose default is the source language does not change the key, so it
  // does not restart the fetcher or reload the transcript. A language switch
  // changes the key and carries the new language and not-started message.
  useEffect(() => {
    getCaptionFetcher().start({
      captionsKey,
      language: captionLanguage,
      notStartedMessage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionsKey]);

  // Fetch the language manifest independently of the caption path. It is absent
  // for captioning-only events and may not exist yet for an early viewer, so
  // retry until it resolves; a null/failed result leaves the client source-only
  // with the caption path untouched.
  useEffect(() => {
    if (liveAsrUniqueKey === undefined) return;
    const abortController = new AbortController();
    let cancelled = false;
    let timeoutId: number | undefined;

    async function loadManifest() {
      try {
        const result = await fetchManifest(
          liveAsrUniqueKey as string,
          abortController.signal
        );
        if (cancelled) return;
        if (result) {
          setManifest(result);
          return;
        }
      } catch {
        if (cancelled) return;
      }
      timeoutId = setTimeout(loadManifest, 5000) as unknown as number;
    }

    loadManifest();

    return () => {
      cancelled = true;
      abortController.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [liveAsrUniqueKey]);

  // Once the manifest resolves, select the default language: the requested
  // ?lang / data-lang when it is one of the event's languages, else source.
  useEffect(() => {
    if (!manifest) return;
    const { source_language, target_languages } = manifest.machine_translation;
    const available = [source_language, ...target_languages];
    const resolved =
      defaultLanguage && available.includes(defaultLanguage)
        ? defaultLanguage
        : source_language;
    accessibilityOptionsDispatch({
      type: "CHANGED_LANGUAGE",
      newValue: resolved,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest]);

  // Reflect the selected language into `?lang=` so a reload preserves the
  // viewer's choice and the URL stays shareable. Only on our own hosts (never a
  // customer-embed page), and only once a language is selected — so no-manifest
  // events never touch the URL.
  useEffect(() => {
    if (selectedLanguage === undefined || !urlParamsEnabled()) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("lang") === selectedLanguage) return;
    url.searchParams.set("lang", selectedLanguage);
    window.history.replaceState(null, "", url);
  }, [selectedLanguage]);

  function handleScrollBottomClick() {
    if (targetEl.current)
      targetEl.current.scrollTop = targetEl.current.scrollHeight;
  }

  const handleTargetScroll = throttle(() => {
    /** Whether or not the user is scrolled to the bottom with a 10px buffer */
    const scrolledToBottom =
      targetEl.current !== null &&
      targetEl.current.scrollHeight - (targetEl.current.scrollTop + 10) <=
        targetEl.current.clientHeight;
    setShowScrollBottomButton(!scrolledToBottom);
    if (showCompleteTranscript)
      setScrollToBottomOnTranscriptChange(scrolledToBottom);
  }, 100);

  // Scroll to the bottom of the caption on transcript change if
  // scrollToBottomOnTranscriptChange is true
  useLayoutEffect(() => {
    if (targetEl.current !== null && scrollToBottomOnTranscriptChange) {
      targetEl.current.scrollTop = targetEl.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  /**
   * The height of the transcript container
   */
  const targetHeight = getTargetHeight({
    fontSize: parseInt(
      accessibilityOptionsState.fontSize.replace("px", ""),
      10
    ),
    lineCount,
    showCompleteTranscript,
    transcriptContainerHeight,
  });

  /**
   * The classNames for the transcript container
   */
  const classNames = [
    // Remove paragraph margins when displaying based off line count
    ["remove-paragraph-margins", !insertParagraphs],
    // Display scroll bar when displaying the complete transcript
    ["show-scroll", showCompleteTranscript],
  ]
    .filter(([, shouldDisplay]) => shouldDisplay)
    .map(([name]) => name)
    .join(" ");

  const accessibilityStyles = omit(accessibilityOptionsState, [
    "displayMode",
    "showTranscriptType",
    "transcriptType",
  ]);

  useEffect(() => {
    const clientContainerElement = document.getElementById("live-asr-client");
    if (clientContainerElement) {
      clientContainerElement.style.color = accessibilityStyles.color;
      clientContainerElement.style.backgroundColor =
        accessibilityStyles.backgroundColor;
    }
  }, [accessibilityStyles.color, accessibilityStyles.backgroundColor]);

  return (
    <>
      {(showAccessibilityMenu || hasTargetLanguages) && (
        <AccessibilityMenu
          accessibilityOptionsDispatch={accessibilityOptionsDispatch}
          accessibilityOptionsState={accessibilityOptionsState}
          languageOptions={languageOptions}
        />
      )}
      {showCompleteTranscript && (
        <div id="live-asr-button-container">
          <button
            className={`btn btn-primary btn-sm ${
              showScrollBottomButton ? "" : "d-none"
            }`}
            onClick={handleScrollBottomClick}
          >
            Scroll to bottom
          </button>
        </div>
      )}
      <div
        id="live-asr-target"
        data-testid="live-asr-target"
        ref={targetEl}
        onScroll={handleTargetScroll}
        className={classNames}
        lang={captionLanguage}
        dir={captionDirection}
        style={{ height: targetHeight, ...accessibilityStyles }}
        // This is being done for performance. See CaptionFetcher for more details.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: transcript }}
      />
      <div id="live-asr-message">{message}</div>
    </>
  );
}
